// THE ZONES VERSION IS RESOLVED BY GEOMETRY, NEVER ASSUMED.
import { useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Loader2, Upload, X } from "lucide-react";
import { api } from "../../api";
import { qk } from "../../hooks/queries";
import { parseCsv, autoMatchColumns, type ParsedCsv } from "../../lib/csv";
import {
  layerFeatureIds,
  layerIdsMissingFromCsv,
  sameZoneGeometry,
  zoneIdIndex,
  zoneKeyLabel,
} from "../../lib/targetResultsMatch";
import { pointRowProblem } from "../../lib/resultProperties";
import { PointColumnMap } from "./PointColumnMap";
import { pointResultsFailure, uploadPointLayerResults } from "./pointResults";
import { previewAnalysis } from "./previewAnalysis";
import { Modal } from "../../ui/Modal";
import {
  labResultErrorMessage,
  partialResultsNotice,
} from "../work-orders/labResults";
import { type UploadResult } from "./UploadResultNote";

export function TargetResultsUpload({
  fieldId,
  layerId,
  title,
  kind,
  fc,
  onClose,
  onUploaded,
  workOrderId,
  onPointsProcessing,
}: {
  fieldId: string;
  layerId: string;
  title: string;
  kind: "points" | "zones";
  fc: GeoJSON.FeatureCollection | null;
  onClose: () => void;
  onUploaded?: (result: UploadResult) => void;
  workOrderId?: string;
  onPointsProcessing?: () => void;
}) {
  const qc = useQueryClient();
  const [file, setFile] = useState<File | null>(null);
  const [parsed, setParsed] = useState<ParsedCsv | null>(null);
  const [mapping, setMapping] = useState<Record<string, number | null> | null>(
    null,
  );
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [statusText, setStatusText] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const targetIds = useMemo(() => layerFeatureIds(fc, kind), [fc, kind]);
  // zones: the CSV may name a zone by its NAME ("North") or its number — both resolve to the server
  // ordinal SS3c attaches by — zones can be named anything.
  const zoneIdx = useMemo(
    () => (kind === "zones" ? zoneIdIndex(fc) : null),
    [fc, kind],
  );

  const analysis = useMemo(
    () =>
      parsed && mapping
        ? previewAnalysis({ parsed, mapping, targetIds, zoneIdx, kind })
        : null,
    [parsed, mapping, targetIds, zoneIdx, kind],
  );

  const pick = async (f: File) => {
    setErr(null);
    setNotice(null);
    setStatusText(null);
    setDone(false);
    setFile(f);
    try {
      const next = parseCsv(await f.text(), f.name);
      setParsed(next);
      setMapping(autoMatchColumns(next));
    } catch (e) {
      setParsed(null);
      setMapping(null);
      setErr(
        e instanceof Error
          ? e.message
          : "That file could not be read as a CSV.",
      );
    }
  };

  const upload = async () => {
    if (!file || !analysis?.verdict?.ok) return;
    setBusy(true);
    setErr(null);
    setNotice(null);
    setStatusText(null);
    try {
      let nextStatus: string | null = null;
      let nextNotice: string | null = null;
      if (kind === "points") {
        const built = analysis.pointRows;
        if (!built) return;
        const problem = pointRowProblem(built);
        if (problem) throw new Error(problem);
        const setId = layerId.replace(/^target-points-/, "");
        const outcome = await uploadPointLayerResults({
          fieldId,
          setId,
          file,
          rowCount: parsed?.rows.length ?? 0,
          title,
          workOrderId,
          rows: built.rows,
        });
        nextNotice = outcome.notice;
        nextStatus = outcome.status;
        if (outcome.asyncFallback && workOrderId) onPointsProcessing?.();
      } else {
        const zr = analysis.zoneRows!;
        if (zr.badZoneIds.length)
          throw new Error(
            `Zone ids must be this layer's zone names or numbers — not: ${zr.badZoneIds.join(", ")}`,
          );
        if (zr.valuelessZones.length)
          throw new Error(
            `No recognised values for zone${zr.valuelessZones.length === 1 ? "" : "s"} ${zr.valuelessZones.join(", ")} — every zone needs at least one reading.`,
          );
        // resolve WHICH server version is the clicked layer, by geometry
        const events = (await api.fetchSampleEvents(fieldId))
          .filter(
            (ev) => ev.kind === "zones" && ev.event_id.startsWith("zones:"),
          )
          .sort((x, y) => y.created_at.localeCompare(x.created_at))
          .slice(0, 8);
        let versionId: string | null = null;
        for (const ev of events) {
          const vid = ev.event_id.slice("zones:".length);
          try {
            const g = await api.fetchZoneVersionGeojson(fieldId, vid);
            if (sameZoneGeometry(g, fc)) {
              versionId = vid;
              break;
            }
          } catch {
            /* a missing version is just not the match */
          }
        }
        if (!versionId) {
          throw new Error(
            "These zones weren’t found on the server — the layer may predate 2026-08-14, when zone saves were repaired. Re-save the zones, then upload the results.",
          );
        }
        const attached = workOrderId
          ? await api.attachZoneResults(fieldId, versionId, zr.rows, {
              work_order_id: workOrderId,
            })
          : await api.attachZoneResults(fieldId, versionId, zr.rows);
        nextNotice = partialResultsNotice(attached);
        nextStatus =
          "Attached — every zone has its readings, and the zone map can be built from them now.";
      }
      void qc.invalidateQueries({ queryKey: qk.sampleEvents(fieldId) });
      void qc.invalidateQueries({ queryKey: qk.samples(fieldId) });
      void qc.invalidateQueries({ queryKey: qk.layers(fieldId) });
      void qc.invalidateQueries({ queryKey: qk.workOrders });
      setNotice(nextNotice);
      setStatusText(nextStatus);
      setDone(true);
      if (nextStatus) {
        onUploaded?.({ status: nextStatus, notice: nextNotice });
        onClose();
      }
    } catch (e) {
      setErr(
        pointResultsFailure(e) ??
          labResultErrorMessage(e) ??
          (e instanceof Error ? e.message : "The upload failed."),
      );
    } finally {
      setBusy(false);
    }
  };

  const v = analysis?.verdict;
  const rowProblem =
    kind === "points" && v?.ok && analysis?.pointRows
      ? pointRowProblem(analysis.pointRows)
      : null;
  const unmatchedPoints =
    kind === "points" && v?.ok && analysis?.ids
      ? layerIdsMissingFromCsv(targetIds, analysis.ids)
      : [];
  // `missingInCsv` holds the layer's ORDINALS, which the user has never seen — those show as the
  // zone's NAME. `extraInCsv`/`duplicateInCsv` can hold ids the CSV said that no zone could be
  // placed from those must echo the CSV's own text.
  const idList = (ids: string[]) =>
    ids
      .slice(0, 12)
      .map((id) => zoneKeyLabel(id, zoneIdx))
      .join(", ") + (ids.length > 12 ? ` … (+${ids.length - 12})` : "");
  const zoneNames = zoneIdx
    ? zoneIdx.ordinals.map((o) => zoneIdx.display.get(o) ?? String(o))
    : [];

  return (
    <Modal>
      <div
        className="fixed inset-0 z-top flex items-center justify-center bg-black/50 p-4"
        onClick={() => {
          if (!busy) onClose();
        }}
      >
        <div
          className="w-full max-w-md rounded-lg bg-surface text-fg shadow-2xl"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center justify-between border-b border-line px-3 py-2">
            <h2 className="text-sm font-semibold">Upload results — {title}</h2>
            <button
              onClick={onClose}
              disabled={busy}
              className="rounded p-1 hover:bg-hover disabled:opacity-50"
              aria-label="Close"
            >
              <X size={16} />
            </button>
          </div>
          <div className="max-h-[70vh] overflow-auto p-3 text-[12px]">
            <p className="mb-2 text-muted">
              {kind === "points"
                ? `A lab CSV whose point IDs are on this layer's ${targetIds.length} points. A point with no row is left without a result. `
                : `A lab CSV whose zone IDs exactly match this layer's ${targetIds.length} zones — by zone name or number. `}
              Column names are recognised automatically (the same vocabulary as
              every other lab import).
            </p>
            {zoneIdx && zoneNames.length > 0 && (
              <p className="mb-2 text-[11px] text-faint">
                Zones:{" "}
                <span className="text-muted">
                  {zoneNames.slice(0, 20).join(", ")}
                  {zoneNames.length > 20
                    ? ` … (+${zoneNames.length - 20})`
                    : ""}
                </span>
              </p>
            )}
            {zoneIdx && zoneIdx.ambiguous.length > 0 && (
              <p className="mb-2 text-[11px] text-amber-400">
                Shared by more than one zone, so they cannot be used as IDs:{" "}
                {zoneIdx.ambiguous.slice(0, 8).join(", ")}
              </p>
            )}
            {targetIds.length === 0 && (
              <p role="alert" className="mb-2 text-red-400">
                This layer's features carry no readable IDs — nothing can be
                matched against.
              </p>
            )}
            <input
              type="file"
              accept=".csv,text/csv"
              aria-label="Lab results CSV"
              disabled={busy}
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) void pick(f);
              }}
              className="mb-2 block w-full text-[11px] text-muted file:mr-2 file:rounded file:border-0 file:bg-accent file:px-2 file:py-1 file:text-[11px] file:font-medium file:text-canvas"
            />
            {parsed?.note && (
              <p role="status" className="mb-2 text-fg">
                {parsed.note}
              </p>
            )}
            {parsed && analysis && (
              <div className="space-y-2">
                {analysis.ids == null && (
                  <p role="alert" className="text-red-400">
                    No Point-ID column was recognised in that file — the upload
                    fails without one.
                  </p>
                )}
                {analysis.analytes.length > 0 && (
                  <p className="text-muted">
                    Recognised:{" "}
                    <span className="text-fg">
                      {analysis.analytes.join(", ")}
                    </span>
                  </p>
                )}
                {kind === "points" && (
                  <PointColumnMap
                    parsed={parsed}
                    mapping={analysis.mapping}
                    onChange={setMapping}
                  />
                )}
                {rowProblem && (
                  <p role="alert" className="text-red-400">
                    {rowProblem}
                  </p>
                )}
                {v && v.ok && (
                  <p className="text-emerald-400">
                    {kind === "points" && unmatchedPoints.length > 0
                      ? `All ${v.count} IDs are on this layer. ${unmatchedPoints.length} ${unmatchedPoints.length === 1 ? "point has" : "points have"} no row.`
                      : `All ${v.count} IDs match this layer exactly.`}
                  </p>
                )}
                {v && !v.ok && (
                  <div
                    role="alert"
                    className="rounded border border-red-500/40 bg-red-500/10 px-2 py-1.5"
                  >
                    <p className="font-medium text-red-400">
                      The IDs don’t match this layer, so the upload fails:
                    </p>
                    <ul className="mt-1 space-y-0.5 text-[11px] text-muted">
                      {v.missingInCsv.length > 0 && (
                        <li>
                          Missing from the CSV:{" "}
                          <span className="text-fg">
                            {idList(v.missingInCsv)}
                          </span>
                        </li>
                      )}
                      {v.extraInCsv.length > 0 && (
                        <li>
                          In the CSV but not this layer:{" "}
                          <span className="text-fg">
                            {idList(v.extraInCsv)}
                          </span>
                        </li>
                      )}
                      {v.duplicateInCsv.length > 0 && (
                        <li>
                          Duplicated in the CSV:{" "}
                          <span className="text-fg">
                            {idList(v.duplicateInCsv)}
                          </span>
                        </li>
                      )}
                      {v.duplicateInLayer.length > 0 && (
                        <li>
                          Duplicated in the layer (ambiguous target):{" "}
                          <span className="text-fg">
                            {idList(v.duplicateInLayer)}
                          </span>
                        </li>
                      )}
                    </ul>
                  </div>
                )}
              </div>
            )}
            {err && (
              <p role="alert" className="mt-2 text-red-400">
                {err}
              </p>
            )}
            {done && statusText && (
              <p role="status" className="mt-2 text-emerald-400">
                {statusText}
              </p>
            )}
            {notice && (
              <p role="status" className="mt-2 text-amber-400">
                {notice}
              </p>
            )}
          </div>
          <div className="flex items-center justify-end gap-2 border-t border-line px-3 py-2">
            <button
              onClick={onClose}
              disabled={busy}
              className="rounded border border-line px-3 py-1 text-[12px] hover:bg-hover disabled:opacity-50"
            >
              Close
            </button>
            <button
              onClick={() => void upload()}
              disabled={
                busy || done || !file || !analysis?.verdict?.ok || !!rowProblem
              }
              className="flex items-center gap-1 rounded bg-accent px-3 py-1 text-[12px] font-medium text-canvas hover:bg-accent-hover disabled:opacity-50"
            >
              {busy ? (
                <Loader2 size={12} className="animate-spin" />
              ) : (
                <Upload size={12} />
              )}{" "}
              Upload
            </button>
          </div>
        </div>
      </div>
    </Modal>
  );
}
